import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalIcon, Video, MapPin, Clock, X, RotateCcw } from "lucide-react";

const ROUNDS = ["Technical Interview","Managerial Interview","HR Interview","Final Interview","Custom Round"];
const TYPES  = ["Video","Phone","In-Person"];

export default function AdminInterviews() {
  const [rows, setRows] = useState([]);
  const [reschOpen, setReschOpen] = useState(null); // interview obj or null
  const [cancelOpen, setCancelOpen] = useState(null);
  const [form, setForm] = useState({ scheduled_at:"", meeting_link:"", duration_min:45, instructions:"", reason:"" });
  const [cancelReason, setCancelReason] = useState("");

  const load = () => api.get("/admin/interviews").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const now = new Date();
  const upcoming = rows.filter(i => i.status !== "cancelled" && new Date(i.scheduled_at) >= now);
  const past = rows.filter(i => i.status === "cancelled" || new Date(i.scheduled_at) < now);

  const reschedule = async () => {
    try {
      await api.put(`/admin/interviews/${reschOpen.id}/reschedule`, form);
      toast.success("Interview rescheduled");
      setReschOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = async () => {
    try {
      await api.put(`/admin/interviews/${cancelOpen.id}/cancel`, { reason: cancelReason });
      toast.success("Interview cancelled");
      setCancelOpen(null); setCancelReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const Row = ({ i }) => (
    <div className="grid md:grid-cols-6 gap-3 items-center py-3 border-b border-slate-800 last:border-0" data-testid={`interview-row-${i.id}`}>
      <div className="md:col-span-2">
        <div className="text-white font-medium">{i.candidate_name}</div>
        <div className="text-xs text-slate-400">{i.job_title}</div>
      </div>
      <div className="text-sm text-slate-300"><CalIcon className="w-3 h-3 inline mr-1"/>{new Date(i.scheduled_at).toLocaleString()}</div>
      <div className="text-sm text-slate-300">{i.round_name}</div>
      <div><Badge className={i.status === "cancelled" ? "bg-red-900/40 text-red-300" : i.status === "rescheduled" ? "bg-amber-900/40 text-amber-300" : "bg-emerald-900/40 text-emerald-300"}>{i.status}</Badge></div>
      <div className="flex gap-2 justify-end">
        {i.status !== "cancelled" && <>
          <Button size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={()=>{ setReschOpen(i); setForm({ scheduled_at: i.scheduled_at, meeting_link: i.meeting_link||"", duration_min: i.duration_min||45, instructions: i.instructions||"", reason:"" }); }} data-testid={`resch-${i.id}`}><RotateCcw className="w-3 h-3 mr-1"/>Reschedule</Button>
          <Button size="sm" variant="outline" className="border-red-800 text-red-300" onClick={()=>setCancelOpen(i)} data-testid={`cancel-${i.id}`}><X className="w-3 h-3 mr-1"/>Cancel</Button>
        </>}
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-white">Interview Calendar</h1>
      <p className="text-slate-400 text-sm mt-1">Manage all scheduled interviews across active jobs.</p>

      <Card className="mt-6 p-6 bg-slate-900 border-slate-800">
        <h3 className="text-lg font-semibold text-white mb-4">Upcoming ({upcoming.length})</h3>
        {upcoming.length === 0 ? <p className="text-slate-400 text-sm">No upcoming interviews.</p> : upcoming.map(i => <Row key={i.id} i={i}/>)}
      </Card>

      <Card className="mt-6 p-6 bg-slate-900 border-slate-800">
        <h3 className="text-lg font-semibold text-white mb-4">Past & Cancelled ({past.length})</h3>
        {past.length === 0 ? <p className="text-slate-400 text-sm">No past interviews.</p> : past.slice(0,20).map(i => <Row key={i.id} i={i}/>)}
      </Card>

      <Dialog open={!!reschOpen} onOpenChange={(o)=>!o && setReschOpen(null)}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>Reschedule Interview</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>New date/time (ISO)</Label><Input type="datetime-local" value={form.scheduled_at?.slice(0,16)||""} onChange={e=>setForm({...form, scheduled_at: new Date(e.target.value).toISOString()})} data-testid="resch-datetime"/></div>
            <div><Label>Duration (min)</Label><Input type="number" value={form.duration_min} onChange={e=>setForm({...form, duration_min: parseInt(e.target.value||"0",10)})}/></div>
            <div><Label>Meeting link (https)</Label><Input value={form.meeting_link} onChange={e=>setForm({...form, meeting_link: e.target.value})}/></div>
            <div><Label>Reason</Label><Textarea rows={2} value={form.reason} onChange={e=>setForm({...form, reason: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setReschOpen(null)}>Cancel</Button>
            <Button onClick={reschedule} className="bg-[#0A2540]" data-testid="resch-submit">Reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelOpen} onOpenChange={(o)=>!o && setCancelOpen(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader><DialogTitle>Cancel Interview</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will notify the candidate by email.</p>
          <div><Label>Reason (optional)</Label><Textarea rows={3} value={cancelReason} onChange={e=>setCancelReason(e.target.value)}/></div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCancelOpen(null)}>Keep interview</Button>
            <Button onClick={cancel} className="bg-red-600 hover:bg-red-700" data-testid="cancel-submit">Cancel Interview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
